import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Landing from "@/pages/Landing";
import ShopPage from "@/pages/ShopPage";
import ProductPage from "@/pages/ProductPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import OrderSuccessPage from "@/pages/OrderSuccessPage";
import OrderCancelledPage from "@/pages/OrderCancelledPage";
import TransferPage from "@/pages/TransferPage";
import ContactPage from "@/pages/ContactPage";
import LegalPage from "@/pages/LegalPage";
import WithdrawalPage from "@/pages/WithdrawalPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminOrderDetailPage from "@/pages/AdminOrderDetailPage";
import BlogIndexPage from "@/pages/BlogIndexPage";
import BlogPostPage from "@/pages/BlogPostPage";
import AwardPage from "@/pages/AwardPage";
import CookieBanner from "@/components/CookieBanner";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { CartProvider } from "@/context/CartContext";
import { AdminAuthProvider } from "@/context/AdminAuthContext";

function FloatingChrome() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  return <WhatsAppFloat />;
}

function App() {
  return (
    <div className="App bg-cream-100 text-charcoal">
      <LanguageProvider>
        <CartProvider>
          <AdminAuthProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<Landing forceLang="es" />} />
                <Route path="/en" element={<Landing forceLang="en" />} />

                {/* Shop — Spanish canonical + English counterpart */}
                <Route path="/tienda" element={<ShopPage forceLang="es" />} />
                <Route path="/shop" element={<ShopPage forceLang="en" />} />

                {/* SEO alias slugs — 301 to canonical shop, no sitemap entry.
                    They were previously indexable duplicates; Google flagged
                    /como-catar-aove as a soft 404 because the URL promises a
                    tasting article but rendered the shop. */}
                <Route
                  path="/aove-cosecha-temprana-granada"
                  element={<Navigate to="/tienda" replace />}
                />
                <Route
                  path="/aove-premium-granada"
                  element={<Navigate to="/tienda" replace />}
                />
                <Route
                  path="/aceite-premium-granada"
                  element={<Navigate to="/tienda" replace />}
                />
                <Route
                  path="/como-catar-aove"
                  element={<Navigate to="/blog/como-catar-aove-en-casa" replace />}
                />

                {/* Product detail — Spanish canonical + English counterpart */}
                <Route path="/producto/:slug" element={<ProductPage forceLang="es" />} />
                <Route path="/product/:slug" element={<ProductPage forceLang="en" />} />
                <Route
                  path="/tienda/aove-500ml"
                  element={<Navigate to="/producto/botella-500ml" replace />}
                />
                <Route
                  path="/tienda/estuche-regalo-aove"
                  element={<Navigate to="/producto/estuche-madera-2x500ml" replace />}
                />
                <Route
                  path="/regalo-gourmet-aove"
                  element={<Navigate to="/producto/estuche-madera-2x500ml" replace />}
                />

                <Route path="/carrito" element={<CartPage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/pedido/exito" element={<OrderSuccessPage />} />
                <Route path="/order/success" element={<OrderSuccessPage />} />
                <Route path="/pedido/cancelado" element={<OrderCancelledPage />} />
                <Route path="/order/cancelled" element={<OrderCancelledPage />} />
                <Route path="/pedido/transferencia" element={<TransferPage />} />
                <Route path="/order/transfer" element={<TransferPage />} />
                <Route path="/contacto" element={<ContactPage forceLang="es" />} />
                <Route path="/contact" element={<ContactPage forceLang="en" />} />
                <Route path="/legal/:kind" element={<LegalPage />} />
                <Route path="/desistimiento" element={<WithdrawalPage />} />
                <Route path="/withdrawal" element={<WithdrawalPage />} />

                {/* Blog / Journal (bilingual, lang is forced by route) */}
                <Route path="/blog" element={<BlogIndexPage forceLang="es" />} />
                <Route path="/blog/:slug" element={<BlogPostPage forceLang="es" />} />
                <Route path="/journal" element={<BlogIndexPage forceLang="en" />} />
                <Route path="/journal/:slug" element={<BlogPostPage forceLang="en" />} />

                {/* Sabor Granada 2023 award — verifiable, no external links */}
                <Route
                  path="/premio-sabor-granada-2023"
                  element={<AwardPage forceLang="es" />}
                />
                <Route
                  path="/en/sabor-granada-award-2023"
                  element={<AwardPage forceLang="en" />}
                />

                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin" element={<AdminDashboardPage />} />
                <Route path="/admin/pedido/:id" element={<AdminOrderDetailPage />} />
                <Route path="/admin/order/:id" element={<AdminOrderDetailPage />} />
              </Routes>
              <CookieBanner />
              <FloatingChrome />
            </BrowserRouter>
          </AdminAuthProvider>
        </CartProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
